import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, BusinessUser } from '../services/api';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { Button } from './design-system';
import { Modal } from './Modal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export type BusinessUserPickerMode = 'single' | 'multiple';
export type BusinessUserPickerSource = 'users' | 'departments';

export interface Department {
  _id: string;
  name: string;
  description?: string;
  businessId: string;
  managerId?: string | {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
  };
  isActive?: boolean;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessUserPickerProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void; // Changed from selectedUserIds to selectedIds for generic use
  mode?: BusinessUserPickerMode; // 'single' or 'multiple'
  source?: BusinessUserPickerSource; // 'users' or 'departments' - defaults to 'users'
  selectedUserIds?: string[]; // Pre-selected user/department IDs (kept for backward compatibility)
  excludeUserIds?: string[]; // User/department IDs to exclude from selection (kept for backward compatibility)
  businessId?: string; // Optional, will use currentBusiness if not provided
  title?: string;
  confirmText?: string;
  showAsModal?: boolean; // If false, renders inline (for use in forms)
  searchPlaceholder?: string;
  emptyMessage?: string;
  filterByRole?: string[]; // Filter users by role (e.g., ['admin', 'manager']) - only applies when source is 'users'
}

export const BusinessUserPicker: React.FC<BusinessUserPickerProps> = ({
  visible,
  onClose,
  onConfirm,
  mode = 'single',
  source = 'users', // Default to 'users' for backward compatibility
  selectedUserIds = [],
  excludeUserIds = [],
  businessId,
  title,
  confirmText,
  showAsModal = true,
  searchPlaceholder,
  emptyMessage,
  filterByRole,
}) => {
  const styles = useThemedStyles(getStyles);
  const { currentBusiness } = useAppStore();
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedUserIds));

  const effectiveBusinessId = businessId || currentBusiness?._id;
  const isMultiple = mode === 'multiple';
  const isUsersSource = source === 'users';
  const isDepartmentsSource = source === 'departments';
  
  // Dynamic titles and placeholders based on source
  const defaultTitle = isUsersSource ? 'Select User' : 'Select Department';
  const defaultSearchPlaceholder = isUsersSource ? 'Search users...' : 'Search departments...';
  const defaultEmptyMessage = isUsersSource 
    ? (searchQuery ? 'No users found' : 'No users available')
    : (searchQuery ? 'No departments found' : 'No departments available');
  const defaultConfirmText = isMultiple 
    ? `Add ${selectedIds.size} ${isUsersSource ? 'User' : 'Department'}${selectedIds.size !== 1 ? 's' : ''}` 
    : 'Select';
  
  const finalTitle = title || defaultTitle;
  const finalSearchPlaceholder = searchPlaceholder || defaultSearchPlaceholder;
  const finalEmptyMessage = emptyMessage || defaultEmptyMessage;

  // Initialize selected IDs from props - use stringified array to prevent unnecessary updates
  const selectedUserIdsString = JSON.stringify(selectedUserIds);
  useEffect(() => {
    if (visible) {
      setSelectedIds(new Set(selectedUserIds));
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, selectedUserIdsString]);

  // Load data when visible and business ID is available
  useEffect(() => {
    if (visible && effectiveBusinessId) {
      if (isUsersSource) {
        loadUsers();
      } else if (isDepartmentsSource) {
        loadDepartments();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, effectiveBusinessId, source]);

  // Memoize excludeUserIds and filterByRole to prevent unnecessary recreations
  const excludeUserIdsString = JSON.stringify(excludeUserIds);
  const filterByRoleString = JSON.stringify(filterByRole);

  const loadUsers = useCallback(async () => {
    if (!effectiveBusinessId || !visible) {
      console.log('⚠️ [BusinessUserPicker] No business ID available or not visible');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getBusinessUsers(effectiveBusinessId);

      if (response.statusCode === 1000 && response.data) {
        let filteredUsers = response.data;

        // Filter by role if specified
        if (filterByRole && filterByRole.length > 0) {
          filteredUsers = filteredUsers.filter((user) =>
            filterByRole.includes(user.role)
          );
        }

        // Filter by search query
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filteredUsers = filteredUsers.filter((user) => {
            const firstName = user.user?.firstName?.toLowerCase() || '';
            const lastName = user.user?.lastName?.toLowerCase() || '';
            const email = user.user?.email?.toLowerCase() || '';
            return (
              firstName.includes(query) ||
              lastName.includes(query) ||
              email.includes(query) ||
              `${firstName} ${lastName}`.includes(query)
            );
          });
        }

        // Exclude specified user IDs
        if (excludeUserIds.length > 0) {
          filteredUsers = filteredUsers.filter(
            (user) => !excludeUserIds.includes(user.userId)
          );
        }

        setUsers(filteredUsers);
      }
    } catch (error: any) {
      console.error('❌ [BusinessUserPicker] Error loading users:', error);
    } finally {
      setLoading(false);
    }
    // Use stringified versions to prevent recreation on array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBusinessId, searchQuery, excludeUserIdsString, filterByRoleString, visible]);

  useEffect(() => {
    if (visible && searchQuery !== undefined) {
      // Debounce search
      const timer = setTimeout(() => {
        if (visible && effectiveBusinessId) {
          if (isUsersSource) {
            loadUsers();
          } else if (isDepartmentsSource) {
            loadDepartments();
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, visible, source]);

  const loadDepartments = useCallback(async () => {
    if (!effectiveBusinessId || !visible) {
      console.log('⚠️ [BusinessUserPicker] No business ID available or not visible');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getDepartments(effectiveBusinessId);

      if (response.statusCode === 1000 && response.data) {
        // Filter only active departments (isActive === true and status === 1)
        let filteredDepartments = response.data.filter((dept: Department) => 
          dept.isActive !== false && dept.status === 1
        );

        // Filter by search query
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filteredDepartments = filteredDepartments.filter((dept: Department) => {
            const name = dept.name?.toLowerCase() || '';
            const description = dept.description?.toLowerCase() || '';
            return name.includes(query) || description.includes(query);
          });
        }

        // Exclude specified department IDs
        if (excludeUserIds.length > 0) {
          filteredDepartments = filteredDepartments.filter(
            (dept: Department) => !excludeUserIds.includes(dept._id)
          );
        }

        setDepartments(filteredDepartments);
      }
    } catch (error: any) {
      console.error('❌ [BusinessUserPicker] Error loading departments:', error);
    } finally {
      setLoading(false);
    }
    // Use stringified versions to prevent recreation on array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBusinessId, searchQuery, excludeUserIdsString, visible]);

  const handleItemToggle = (itemId: string) => {
    if (mode === 'single') {
      setSelectedIds(new Set([itemId]));
      // Auto-confirm for single mode
      onConfirm([itemId]);
      onClose();
      return;
    }

    // Multiple mode
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    if (selectedIds.size === 0) {
      return;
    }
    onConfirm(Array.from(selectedIds));
    onClose();
  };

  const getUserDisplayName = (user: BusinessUser) => {
    const firstName = user.user?.firstName || '';
    const lastName = user.user?.lastName || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim() || 'Unknown User';
    }
    return user.user?.email || 'Unknown User';
  };

  const getUserInitials = (user: BusinessUser) => {
    const firstName = user.user?.firstName || '';
    const lastName = user.user?.lastName || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (user.user?.email) {
      return user.user.email[0].toUpperCase();
    }
    return 'U';
  };

  const getDepartmentDisplayName = (dept: Department) => {
    return dept.name || 'Unknown Department';
  };

  const getDepartmentInitials = (dept: Department) => {
    const name = dept.name || '';
    if (name.length >= 2) {
      return name.substring(0, 2).toUpperCase();
    }
    if (name.length === 1) {
      return name[0].toUpperCase();
    }
    return 'D';
  };

  const getManagerDisplayName = (managerId?: string | {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
  }) => {
    if (!managerId) return null;
    if (typeof managerId === 'string') return null; // Just an ID, can't display
    if (typeof managerId === 'object') {
      const firstName = managerId.firstName || '';
      const lastName = managerId.lastName || '';
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
      return managerId.email || managerId.username || null;
    }
    return null;
  };

  const renderUserItem = ({ item }: { item: BusinessUser }) => {
    const isSelected = selectedIds.has(item.userId);
    const displayName = getUserDisplayName(item);
    const initials = getUserInitials(item);

    return (
      <TouchableOpacity
        style={[styles.userItem, isSelected && styles.userItemSelected]}
        onPress={() => handleItemToggle(item.userId)}
        activeOpacity={0.7}
      >
        <View style={styles.userAvatar}>
          {initials ? (
            <Text style={styles.userAvatarText}>{initials}</Text>
          ) : (
            <Text style={styles.userAvatarText}>U</Text>
          )}
        </View>
        <View style={styles.userInfo}>
          {displayName && displayName.trim() ? (
            <Text style={styles.userName}>{displayName}</Text>
          ) : (
            <Text style={styles.userName}>Unknown User</Text>
          )}
          {item.user?.email && item.user.email.trim() ? (
            <Text style={styles.userEmail}>{item.user.email}</Text>
          ) : null}
          {item.role && item.role.trim() ? (
            <Text style={styles.userRole}>Role: {item.role}</Text>
          ) : null}
        </View>
        <View style={styles.userCheckbox}>
          {isSelected ? (
            <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
          ) : (
            <Ionicons name="ellipse-outline" size={24} color={colors.border} />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderDepartmentItem = ({ item }: { item: Department }) => {
    const isSelected = selectedIds.has(item._id);
    const displayName = getDepartmentDisplayName(item);
    const initials = getDepartmentInitials(item);
    const managerName = getManagerDisplayName(item.managerId);

    return (
      <TouchableOpacity
        style={[styles.userItem, isSelected && styles.userItemSelected]}
        onPress={() => handleItemToggle(item._id)}
        activeOpacity={0.7}
      >
        <View style={[styles.userAvatar, styles.departmentAvatar]}>
          <Ionicons name="business-outline" size={24} color={colors.primary} />
        </View>
        <View style={styles.userInfo}>
          {displayName && displayName.trim() ? (
            <Text style={styles.userName}>{displayName}</Text>
          ) : (
            <Text style={styles.userName}>Unknown Department</Text>
          )}
          {item.description && item.description.trim() ? (
            <Text style={styles.userEmail}>{item.description}</Text>
          ) : null}
          {managerName ? (
            <Text style={styles.userRole}>Manager: {managerName}</Text>
          ) : null}
        </View>
        <View style={styles.userCheckbox}>
          {isSelected ? (
            <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
          ) : (
            <Ionicons name="ellipse-outline" size={24} color={colors.border} />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderItem = ({ item }: { item: BusinessUser | Department }) => {
    if (isUsersSource) {
      return renderUserItem({ item: item as BusinessUser });
    } else {
      return renderDepartmentItem({ item: item as Department });
    }
  };

  const getData = () => {
    return isUsersSource ? users : departments;
  };

  const getKeyExtractor = (item: BusinessUser | Department) => {
    return isUsersSource ? (item as BusinessUser).userId : (item as Department)._id;
  };


  if (!showAsModal) {
    // Inline mode - just render content
    if (!visible) return null;
    return (
      <View style={styles.inlineContainer}>
        {/* Search */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
            <TextInput
              placeholder={finalSearchPlaceholder}
              value={searchQuery}
              onChangeText={setSearchQuery}
              style={styles.searchInput}
              placeholderTextColor={colors.textLight}
            />
          </View>
        </View>

        {/* Items List */}
        {loading && getData().length === 0 ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>
              {isUsersSource ? 'Loading users...' : 'Loading departments...'}
            </Text>
          </View>
        ) : getData().length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons 
              name={isUsersSource ? "people-outline" : "business-outline"} 
              size={64} 
              color={colors.textLight} 
            />
            <Text style={styles.emptyText}>
              {finalEmptyMessage}
            </Text>
            {searchQuery && (
              <Text style={styles.emptySubtext}>Try a different search term</Text>
            )}
          </View>
        ) : (
          <FlatList
            data={getData()}
            renderItem={renderItem}
            keyExtractor={getKeyExtractor}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            style={styles.list}
          />
        )}
      </View>
    );
  }

  // Modal mode - use Modal component
  const modalTitle = isMultiple && selectedIds.size > 0
    ? `${finalTitle} (${selectedIds.size} selected)`
    : finalTitle;

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      title={modalTitle}
      size="large"
      scrollable={true}
    >
      <View style={styles.modalContent}>
        {/* Search */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
            <TextInput
              placeholder={finalSearchPlaceholder}
              value={searchQuery}
              onChangeText={setSearchQuery}
              style={styles.searchInput}
              placeholderTextColor={colors.textLight}
            />
          </View>
        </View>

        {/* Items List */}
        {loading && getData().length === 0 ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>
              {isUsersSource ? 'Loading users...' : 'Loading departments...'}
            </Text>
          </View>
        ) : getData().length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons 
              name={isUsersSource ? "people-outline" : "business-outline"} 
              size={64} 
              color={colors.textLight} 
            />
            <Text style={styles.emptyText}>
              {finalEmptyMessage}
            </Text>
            {searchQuery && (
              <Text style={styles.emptySubtext}>Try a different search term</Text>
            )}
          </View>
        ) : (
          <FlatList
            data={getData()}
            renderItem={renderItem}
            keyExtractor={getKeyExtractor}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            style={styles.list}
          />
        )}

        {/* Footer Actions - Only show for multiple mode */}
        {isMultiple && (
          <View style={styles.footerActions}>
            <Button
              title="Cancel"
              onPress={onClose}
              variant="outline"
              style={styles.cancelButton}
            />
            <Button
              title={confirmText || defaultConfirmText}
              onPress={handleConfirm}
              variant="primary"
              disabled={selectedIds.size === 0}
              style={styles.confirmButton}
            />
          </View>
        )}
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  inlineContainer: {
    flex: 1,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  modalContent: {
    flex: 1,
    minHeight: 400,
  },
  searchContainer: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
  },
  searchIcon: {
    marginRight: spacing.xs,
  },
  searchInput: {
    flex: 1,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  list: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
    flexGrow: 1,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  userItemSelected: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  userAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: withAlpha(colors.primary, 0.13),
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  departmentAvatar: {
    backgroundColor: withAlpha(colors.primary, 0.13),
  },
  userAvatarText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  userEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    marginBottom: spacing.xs / 2,
  },
  userRole: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    textTransform: 'capitalize',
  },
  userCheckbox: {
    marginLeft: spacing.sm,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs,
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      bottom: 0,
    }),
  },
  cancelButton: {
    flex: 1,
  },
  confirmButton: {
    flex: 1,
  },
});
